import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Plus, Trash2, ShoppingBag, Pencil, X,
  UploadCloud, Package, Search, ChevronLeft, ChevronRight, 
  Percent
} from "lucide-react";

import api from "./axiosConfig";
import { useLanguage } from "./LanguageContext";

export default function MerchantProducts() {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showPromoModal, setShowPromoModal] = useState(false);
  const [promoForm, setPromoForm] = useState({ category: "", discount: "" });
  const [isApplyingPromo, setIsApplyingPromo] = useState(false);
  
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");

  const [page, setPage] = useState(1);
  const [limit] = useState(24); 
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [form, setForm] = useState({
    name: "", price: "", stock: "", description: "", image: null,
    category: "Autres", customCategory: "",
  });

  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "", price: "", stock: "", image: null,
    category: "Autres", customCategory: "",
  });

  useEffect(() => { fetchProducts(); }, [page]);

  useEffect(() => {
    setPage(1);
    if (page === 1) fetchProducts(); 
  }, [searchTerm, selectedCategory]);

  const fetchProducts = async () => {
    try {
      const res = await api.get("/products/me", {
        params: { 
          page, 
          limit, 
          search: searchTerm || "",
          category: selectedCategory || ""
        },
      });
      setProducts(res.data.products || []);
      
      if (res.data.categories) {
        setCategories(res.data.categories);
      }

      setTotal(Number(res.data.total || 0));
      setTotalPages(Number(res.data.totalPages || 1));
    } catch (err) {
      console.error("Erreur fetch produits marchand:", err);
    } finally {
      setLoading(false);
    }
  };

  const addProduct = async (e) => {
    e.preventDefault();
    const finalCategory = form.category === "custom" && form.customCategory.trim()
        ? form.customCategory.trim() : form.category || "Autres";

    const tempId = `tmp-${Date.now()}`;
    const tempProduct = {
      _id: tempId, name: form.name, price: Number(form.price), stock: Number(form.stock),
      category: finalCategory, image: form.image ? URL.createObjectURL(form.image) : "",
    };

    setProducts((p) => [tempProduct, ...(p || [])]);

    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("price", form.price);
      fd.append("stock", form.stock);
      fd.append("category", finalCategory);
      
      if (form.image) {
        const ext = form.image.type === "image/png" ? "png" : "jpg";
        const safeName = `produit_${Date.now()}.${ext}`;
        fd.append("image", form.image, safeName);
      }

      await api.post("/products", fd);
      setPage(1);
      fetchProducts(); 
      setForm({ name: "", price: "", stock: "", description: "", image: null, category: "Autres", customCategory: "" });
    } catch (err) {
      setProducts((p) => (p || []).filter((x) => x._id !== tempId));
      alert("Erreur lors de l’ajout. Vérifiez votre connexion.");
    }
  };

  const updateProduct = async () => {
    const backup = [...products];
    const finalCategory = editForm.category === "custom" && editForm.customCategory.trim()
        ? editForm.customCategory.trim() : editForm.category || "Autres";

    try {
      const fd = new FormData();
      fd.append("name", editForm.name);
      fd.append("price", editForm.price);
      fd.append("stock", editForm.stock);
      fd.append("category", finalCategory);
      
      // La logique sécurisée est ici aussi, elle enverra l'image à l'API Souqi
      if (editForm.image) {
        const ext = editForm.image.type === "image/png" ? "png" : "jpg";
        const safeName = `produit_edit_${Date.now()}.${ext}`;
        fd.append("image", editForm.image, safeName);
      }

      await api.put(`/products/${editing}`, fd);
      setEditing(null);
      fetchProducts();
    } catch {
      setProducts(backup);
      alert("Erreur modification");
    }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm(t("delete_confirm"))) return;
    const backup = [...products];
    setProducts((p) => (p || []).filter((x) => x._id !== id));
    try {
      await api.delete(`/products/${id}`);
      fetchProducts();
    } catch {
      setProducts(backup);
      alert("Erreur suppression");
    }
  };

  const handleApplyDiscount = async () => {
    if (!promoForm.category || promoForm.discount === "") {
      alert("Veuillez sélectionner une catégorie et un pourcentage.");
      return;
    }
  
    try {
      setIsApplyingPromo(true);
      await api.post("/products/bulk-discount", {
        category: promoForm.category,
        discountPercentage: Number(promoForm.discount)
      });
      
      alert("Mise à jour réussie !");
      setShowPromoModal(false);
      setPromoForm({ category: "", discount: "" });
      fetchProducts(); // Rafraîchir la liste
    } catch (err) {
      alert("Erreur lors de l'application de la promo");
    } finally {
      setIsApplyingPromo(false);
    }
  };
  
  const getImageUrl = (imgData) => {
    if (!imgData) return null;
    if (typeof imgData !== "string") return URL.createObjectURL(imgData);
    if (imgData.startsWith("http") || imgData.startsWith("blob:") || imgData.startsWith("data:")) {
      return imgData;
    }
    return `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/${imgData.replace(/^\//, '')}`;
  };

  const handleCategoryClick = (cat) => {
    setSelectedCategory(cat === selectedCategory ? "" : cat);
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen bg-[#F8FAFC]">
      <Package className="text-[#f34141] animate-spin" size={32} />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-12">
      <header className="bg-[#f34141] py-8 px-6 rounded-b-[2.5rem] shadow-lg relative z-10">
        <div className="flex flex-col items-start justify-between max-w-6xl gap-6 mx-auto md:flex-row md:items-center">
          <div className="text-white">
            <button 
              onClick={() => navigate("/merchant/dashboard")}
              className="flex items-center gap-2 px-3 py-1.5 mb-4 text-[10px] font-black uppercase tracking-widest bg-white/10 hover:bg-white/20 rounded-lg transition-all border border-white/5"
            >
              <ArrowLeft size={14} /> {t("back")} Dashboard
            </button>
            <h1 className="flex items-center gap-3 text-3xl font-black tracking-tighter">
              <ShoppingBag size={28} /> {t("navProducts")}
            </h1>
          </div>
          
          <div className="flex gap-3">
             <div className="bg-black/10 backdrop-blur-md px-5 py-3 rounded-2xl border border-white/10 text-white min-w-[100px]">
                <p className="text-[9px] uppercase font-black opacity-60 tracking-widest mb-1">{t("navProducts")}</p>
                <p className="text-2xl font-black leading-none">{total}</p>
             </div>
             <div className="bg-black/10 backdrop-blur-md px-5 py-3 rounded-2xl border border-white/10 text-white min-w-[100px]">
                <p className="text-[9px] uppercase font-black opacity-60 tracking-widest mb-1">{t("category")}</p>
                <p className="text-2xl font-black leading-none">{categories.length}</p>
             </div>
             <button 
               onClick={() => setShowPromoModal(true)}
               className="bg-white/10 backdrop-blur-md px-5 py-3 rounded-2xl border border-white/20 text-white hover:bg-white/20 transition-all flex flex-col justify-center"
             >
                <p className="text-[9px] uppercase font-black opacity-60 tracking-widest mb-1">Actions</p>
                <div className="flex items-center gap-2">
                  <Percent size={18} className="text-yellow-300" />
                  <span className="text-sm font-black uppercase">Promotions</span>
                </div>
             </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl px-4 mx-auto mt-10 space-y-10">
        <section className="p-8 bg-white border shadow-sm rounded-3xl border-slate-100">
          <h2 className="text-xs font-black uppercase tracking-[0.2em] text-slate-400 mb-8 flex items-center gap-2">
            <Plus size={18} strokeWidth={3} className="text-[#f34141]" /> {t("add_product")}
          </h2>

          <form onSubmit={addProduct} className="grid items-end gap-6 md:grid-cols-4">
            <InputField label={t("product_name")} placeholder="Ex: Nutella 400g" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <InputField label={t("price_mru")} type="number" placeholder="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
            <InputField label={t("stock")} type="number" placeholder="0" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} />
            
            <div className="flex flex-col gap-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider">{t("category")}</label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full px-4 py-3 border border-slate-100 rounded-xl bg-slate-50 text-sm font-bold outline-none focus:ring-2 focus:ring-[#f34141]/10 focus:border-[#f34141]"
              >
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                <option value="custom">{t("new_category")}</option>
              </select>
            </div>

            {form.category === "custom" && (
              <div className="md:col-span-2">
                <InputField label={t("category")} placeholder="Ex: Boissons" value={form.customCategory} onChange={(e) => setForm({ ...form, customCategory: e.target.value })} />
              </div>
            )}

            <div className="md:col-span-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-2 block">{t("image_product")}</label>
              <label className="flex items-center gap-4 px-4 py-2.5 border-2 border-dashed border-slate-200 rounded-xl cursor-pointer hover:bg-slate-50 transition-all group">
                <UploadCloud size={20} className="text-slate-400 group-hover:text-[#f34141]" />
                <span className="text-xs font-bold truncate text-slate-500">
                  {form.image ? "✅ Image sélectionnée" : t("select_image")}
                </span>
                <input 
                  type="file" 
                  accept="image/jpeg,image/png,image/jpg" 
                  className="hidden" 
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) setForm({ ...form, image: file });
                  }} 
                />
              </label>
            </div>

            <button className="md:col-span-1 bg-[#f34141] text-white py-3.5 rounded-xl font-black uppercase tracking-widest text-[11px] hover:bg-[#d63232] transition-all shadow-lg shadow-red-50 hover:scale-[1.02] active:scale-95">
              {t("save")}
            </button>
          </form>
        </section>

        <section>
          <div className="flex flex-col items-end justify-between gap-4 mb-4 md:flex-row">
            <div>
              <h3 className="text-2xl font-black tracking-tighter text-slate-900">{t("inventory")}</h3>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em] mt-1">{t("display_count").replace("{count}", products.length)}</p>
            </div>
            <div className="relative w-full md:w-80">
              <Search className="absolute -translate-y-1/2 left-4 top-1/2 text-slate-300" size={18} />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t("search_product")}
                className="w-full py-3 pl-12 pr-4 text-sm font-bold transition-all bg-white border shadow-sm outline-none border-slate-100 rounded-2xl focus:ring-4 focus:ring-slate-50"
              />
            </div>
          </div>

          {categories.length > 0 && (
            <div className="flex gap-2 pb-4 mb-4 overflow-x-auto scrollbar-hide">
               <button
                  onClick={() => handleCategoryClick("")}
                  className={`px-4 py-2 rounded-full whitespace-nowrap text-[11px] font-black uppercase tracking-wider transition-all ${
                    selectedCategory === ""
                      ? "bg-[#f34141] text-white shadow-md shadow-red-100"
                      : "bg-white border border-slate-100 text-slate-400 hover:bg-slate-50"
                  }`}
                >
                  TOUT
                </button>
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => handleCategoryClick(cat)}
                  className={`px-4 py-2 rounded-full whitespace-nowrap text-[11px] font-black uppercase tracking-wider transition-all ${
                    selectedCategory === cat
                      ? "bg-[#f34141] text-white shadow-md shadow-red-100"
                      : "bg-white border border-slate-100 text-slate-400 hover:bg-slate-50"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {products.map((p) => (
              <div key={p._id} className="bg-white p-5 rounded-[2rem] border border-slate-100 shadow-sm flex flex-col gap-5 hover:shadow-xl transition-all group">
                <div className="flex gap-5">
                    <div className="relative flex-shrink-0">
                    <img
                        src={getImageUrl(p.image)}
                        alt={p.name}
                        className="w-24 h-24 object-cover rounded-[1.5rem] bg-slate-50"
                        onError={(e) => { e.target.src = "https://via.placeholder.com/150?text=Produit"; }}
                    />
                    {p.outOfStock && (
                        <div className="absolute inset-0 bg-white/80 backdrop-blur-[1px] rounded-[1.5rem] flex items-center justify-center border border-red-100">
                        <span className="text-[10px] font-black text-red-600 uppercase tracking-tighter">{t("out_of_stock")}</span>
                        </div>
                    )}
                    </div>

                    <div className="flex-1 min-w-0 py-1">
                      <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest bg-slate-50 px-2 py-0.5 rounded-full">
                        {p.category}
                      </span>
                      <h4 className="mt-1 text-lg font-black tracking-tight truncate text-slate-800">
                        {p.name}
                      </h4>
                      <div className="flex items-baseline flex-wrap gap-2">
                        <span className="text-xl font-black text-[#f34141]">
                          {p.price}
                        </span>
                        {p.discountPercentage > 0 && (
                          <span className="text-[12px] font-bold text-slate-300 line-through decoration-slate-300">
                            {p.originalPrice}
                          </span>
                        )}
                        <span className="text-[10px] font-black text-slate-400 uppercase">
                          MRU
                        </span>
                        {p.discountPercentage > 0 && (
                          <span className="bg-yellow-100 text-yellow-700 text-[9px] font-black px-1.5 py-0.5 rounded-md border border-yellow-200">
                            -{p.discountPercentage}%
                          </span>
                        )}
                      </div>
                      <p className="text-[10px] font-bold text-slate-400 mt-1 uppercase">
                        {t("stock")}: {p.stock}
                      </p>
                    </div>
                </div>

                <div className="flex items-center gap-2 pt-4 border-t border-slate-50">
                  <button 
                    onClick={() => { setEditing(p._id); setEditForm({ ...p, image: null }); }}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-slate-50 hover:bg-slate-100 text-slate-500 rounded-xl transition-colors font-black text-[10px] uppercase tracking-widest"
                  >
                    <Pencil size={14} /> {t("edit")}
                  </button>

                  <button 
                    onClick={async () => {
                      try {
                        setProducts((list) => list.map((x) => x._id === p._id ? { ...x, outOfStock: !x.outOfStock } : x));
                        await api.patch(`/products/${p._id}/toggle-out-of-stock`);
                      } catch (err) { fetchProducts(); }
                    }}
                    className={`flex-1 py-2.5 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all ${
                      p.outOfStock ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-600"
                    }`}
                  >
                    {p.outOfStock ? t("restock") : t("rupture")}
                  </button>

                  <button 
                    onClick={() => deleteProduct(p._id)}
                    className="p-2.5 text-slate-300 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between p-4 mt-12 bg-white border shadow-sm rounded-2xl border-slate-50">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="flex items-center gap-2 px-4 py-2 disabled:opacity-20 font-black text-[10px] uppercase tracking-widest"><ChevronLeft size={16}/> {t("prev")}</button>
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">{t("page")} {page} / {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="flex items-center gap-2 px-4 py-2 disabled:opacity-20 font-black text-[10px] uppercase tracking-widest text-[#f34141]">{t("next")} <ChevronRight size={16}/></button>
          </div>
        </section>
      </div>

      {/* MODAL D'ÉDITION */}
      {editing && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white w-full max-w-md p-8 rounded-[2.5rem] shadow-2xl animate-in zoom-in duration-200 overflow-y-auto max-h-[90vh]">
            <div className="flex items-center justify-between mb-8">
              <h3 className="text-xl font-black tracking-tighter">{t("edit_product")}</h3>
              <button onClick={() => setEditing(null)} className="p-2 transition-colors rounded-full hover:bg-slate-50"><X size={24}/></button>
            </div>
            
            <div className="space-y-5">
              <InputField label={t("product_name")} value={editForm.name} onChange={(e) => setEditForm({...editForm, name: e.target.value})} />
              
              <div className="grid grid-cols-2 gap-4">
                <InputField label={t("price_mru")} type="number" value={editForm.price} onChange={(e) => setEditForm({...editForm, price: e.target.value})} />
                <InputField label={t("stock")} type="number" value={editForm.stock} onChange={(e) => setEditForm({...editForm, stock: e.target.value})} />
              </div>
              
              <div className="flex flex-col w-full gap-2">
                 <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider">{t("category")}</label>
                 <select
                    value={editForm.category}
                    onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                    className="w-full px-4 py-3 border border-slate-100 rounded-xl bg-slate-50 text-sm font-bold outline-none focus:ring-2 focus:ring-[#f34141]/10 focus:border-[#f34141]"
                  >
                    {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                    <option value="custom">{t("new_category")}</option>
                  </select>
              </div>
       
               {editForm.category === "custom" && (
                  <InputField label={t("category")} value={editForm.customCategory} onChange={(e) => setEditForm({ ...editForm, customCategory: e.target.value })} />
               )}
              
              <div className="flex flex-col w-full gap-2 mt-4">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Modifier l'image (Optionnel)</label>
                <label className="flex items-center gap-4 px-4 py-3 border-2 border-dashed border-slate-200 rounded-xl cursor-pointer hover:bg-slate-50 transition-all group">
                  <UploadCloud size={20} className="text-slate-400 group-hover:text-[#f34141]" />
                  <span className="text-xs font-bold truncate text-slate-500">
                    {editForm.image ? "✅ Nouvelle image sélectionnée" : "Cliquer pour changer l'image"}
                  </span>
                  <input 
                    type="file" 
                    accept="image/jpeg,image/png,image/jpg" 
                    className="hidden" 
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) setEditForm({ ...editForm, image: file });
                    }} 
                  />
                </label>
              </div>
      
              <div className="flex flex-col gap-3 mt-8">
                <button onClick={updateProduct} className="w-full bg-[#f34141] text-white py-4 rounded-2xl font-black uppercase tracking-widest text-xs shadow-lg shadow-red-100">{t("save_changes")}</button>
                <button onClick={() => setEditing(null)} className="w-full py-3 text-slate-400 font-bold text-[10px] uppercase tracking-widest">{t("cancel")}</button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* MODAL PROMOTIONS (Mise à jour avec sélection de catégorie) */}
      {showPromoModal && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md">
          <div className="bg-white w-full max-w-md p-8 rounded-[2.5rem] shadow-2xl animate-in zoom-in duration-200">
            <div className="flex items-center justify-between mb-8">
              <h3 className="text-xl font-black tracking-tighter">Réductions groupées</h3>
              <button onClick={() => setShowPromoModal(false)} className="p-2 transition-colors rounded-full hover:bg-slate-50">
                <X size={24}/>
              </button>
            </div>
            
            <div className="space-y-6">
              {/* Nouveau : Sélection de la catégorie concernée */}
              <div className="flex flex-col w-full gap-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider">
                  Catégorie à promotionner
                </label>
                <select
                  value={promoForm.category}
                  onChange={(e) => setPromoForm({ ...promoForm, category: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-100 rounded-xl bg-slate-50 text-sm font-bold outline-none focus:ring-2 focus:ring-black/5 focus:border-black transition-all"
                >
                  <option value="">Choisir une catégorie...</option>
                  {categories.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              {/* Champ pourcentage */}
              <InputField 
                label="Pourcentage de réduction (%)" 
                type="number" 
                placeholder="Ex: 10"
                value={promoForm.discount} 
                onChange={(e) => setPromoForm({...promoForm, discount: e.target.value})} 
              />
              
              <button 
                onClick={handleApplyDiscount} 
                className="w-full bg-black text-white py-4 rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-slate-800 transition-all shadow-lg active:scale-[0.98]"
              >
                Appliquer la réduction
              </button>
              
              <p className="text-[10px] text-center text-slate-400 font-medium px-4">
                Note : Cette action modifiera le prix de tous les produits de la catégorie sélectionnée.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InputField({ label, ...props }) {
  return (
    <div className="flex flex-col w-full gap-2">
      <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider">{label}</label>
      <input 
        {...props} 
        className="px-4 py-3 border border-slate-100 rounded-xl bg-slate-50 text-sm font-bold outline-none focus:ring-2 focus:ring-[#f34141]/10 focus:border-[#f34141] transition-all placeholder:text-slate-300" 
      />
    </div>
  );
}
